import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, Subject } from 'rxjs';
import { finalize, map, takeUntil } from 'rxjs/operators';
import { DataService } from 'src/app/helpers/data.service';
import { iCompanyResult } from 'src/app/helpers/Interfaces/company';
import { iEmpStationsResult } from 'src/app/helpers/Interfaces/emp-stations';
import { iStationResult } from 'src/app/helpers/Interfaces/station';
import { Nullable } from 'src/app/helpers/Types/types';
import { CompanyService } from 'src/app/modules/setup/company/company.service';
import { StationService } from 'src/app/modules/setup/station/station.service';

@Component({
  selector: 'app-second-navbar',
  templateUrl: './second-navbar.component.html',
  styleUrls: ['./second-navbar.component.scss'],
})
export class SecondNavbarComponent implements OnInit, OnDestroy {
  @ViewChild('dropdown')
  set dropdown(value: NgbDropdown | undefined) {
    if (!value) return;

    value.openChange.pipe(takeUntil(this.unsubscribe$)).subscribe((v) => {
      if (!v) {
        this.currentSelectedStation = this.selectedStation;
      }
    });
  }
  @Output('onStationChange') onStationChange =
    new EventEmitter<iStationResult>();
  companies: iCompanyResult[] = [];
  stations: iStationResult[] = [];
  private empStations: iEmpStationsResult[] = [];
  loading = {
    companies: false,
    stations: false,
    empStations: false,
  };
  showBar = false;
  inputsLoading = true;
  selectedCompany: any = null;
  selectedStation: any = null;
  currentSelectedStation: any = null;
  tempSelectedCompany: any = null;
  tempSelectedStation: any = null;
  private unsubscribe$ = new Subject<void>();
  applying = false;
  constructor(
    private companyService: CompanyService,
    private stationService: StationService,
    private toastrService: ToastrService,
    private dataService: DataService
  ) { }
  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  handleCompanyChange(company: Nullable<iCompanyResult>, flag?: boolean): void {
    this.dataService.setCompanyId(company?.id);
    if (!company && !flag) this.handleStationChange(null);
    this.reloadIfNeeded();
  }

  handleStationChange(station: Nullable<iStationResult>): void {
    this.dataService.setStationId(station?.id);
    if (!station) this.dataService.setCompanyId(undefined);
    const associatedCompany = station
      ? this.companies.find((c) => c.id === station.companyId)
      : null;

    if (associatedCompany) this.handleCompanyChange(associatedCompany, true);
    else this.reloadIfNeeded();
  }

  applyFilter() {
    if (
      !this.currentSelectedStation ||
      this.currentSelectedStation.id != this.selectedStation?.id
    ) {
      this.applying = true;
      this.handleStationChange(this.currentSelectedStation);
    }
  }

  private initializeSelections(): void {
    const employeeValue = this.dataService.employeeValue;
    if (this.dataService.companyIdValue) {
      this.tempSelectedCompany = this.dataService.companyIdValue;
    } else if (this.dataService.hasPermission('L1008')) {
      this.tempSelectedCompany = null;
    } else {
      this.tempSelectedCompany = employeeValue?.companyId || null;
    }

    if (this.dataService.stationIdValue) {
      this.tempSelectedStation = this.dataService.stationIdValue;
    } else if (this.dataService.hasPermission('L1070')) {
      this.tempSelectedStation = null;
    } else if (this.empStations.length <= 1) {
      this.tempSelectedStation = employeeValue?.stationId || null;
    }

    this.inputsLoading = false;
  }

  private initializeComponent(): void {
    combineLatest([
      this.dataService.permissions$,
      this.dataService.empStations$,
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: ([permissions, empStations]) => {
          this.empStations = empStations;
          if (permissions) {
            this.initializeSelections();
          }
          this.showBar = this.shouldShowBar(permissions, empStations);

          if (this.showBar) {
            this.fetchCompanies();
          }
        },
        error: () => {
          this.toastrService.error('Error initializing component.');
        },
      });
  }

  private shouldShowBar(
    permissions: any,
    empStations: iEmpStationsResult[]
  ): boolean {
    return (
      this.dataService.hasPermission('L1008') ||
      this.dataService.hasPermission('L1070') ||
      empStations.length > 1
    );
  }

  private shouldFetchStations(): boolean {
    return (
      this.dataService.hasPermission('L1070') ||
      !!this.selectedCompany ||
      this.empStations.length > 0
    );
  }

  private fetchCompanies(): void {
    this.loading.companies = true;
    this.companyService
      .getAll()
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loading.companies = false;
          if (this.shouldFetchStations()) {
            this.fetchStations();
          }
        })
      )
      .subscribe({
        next: (companies) => {
          this.companies = companies;
          if (this.tempSelectedCompany)
            this.selectedCompany = this.companies.find(
              (c) => c.id === this.tempSelectedCompany
            );
          this.selectCompanyIfNeeded();
        },
        error: () => {
          this.toastrService.error('Error fetching companies.');
        },
      });
  }

  private fetchStations(): void {
    this.loading.stations = true;
    this.stationService
      .getAll(undefined, true)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => (this.loading.stations = false)),
        map((stations) => {
          return stations.filter((x) =>
            this.companies.some((y) => y.id == x.companyId)
          );
        })
      )
      .subscribe({
        next: (stations) => {
          this.stations = this.dataService.hasPermission('L1070')
            ? stations
            : this.filterStationsByEmpStations(stations);
          if (this.tempSelectedStation)
            this.currentSelectedStation = this.selectedStation =
              this.stations.find((s) => s.id === this.tempSelectedStation);
          this.onStationChange.emit(this.selectedStation);
          this.selectCompanyIfNeeded();
        },
        error: () => {
          this.toastrService.error('Error fetching stations.');
        },
      });
  }

  private filterStationsByEmpStations(
    stations: iStationResult[]
  ): iStationResult[] {
    const empStationIds = this.empStations.map((es) => es.stationId);
    return stations.filter((station) => empStationIds.includes(station.id));
  }
  private selectCompanyIfNeeded(): void {
    if (this.tempSelectedStation && !this.tempSelectedCompany) {
      this.selectedCompany = this.companies.find(
        (cc) =>
          cc.id ===
          this.stations.find((ss) => ss.id === this.tempSelectedStation)
            ?.companyId
      );
      this.dataService.setCompanyId(this.selectedCompany.id);
    }
  }
  private reloadIfNeeded(): void {
    setTimeout(() => location.reload(), 300);
  }
}
